import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { LocalStorageHelper } from '../helpers/local-storage.helper';
import { CommonService } from '../common.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated: boolean = false;
  private _userRole: string | null = null;

  constructor(
    private localStorage: LocalStorageHelper,
    private _commonService: CommonService,
    private _httpClient: HttpClient,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {}

  setToken(token: string) {
    this.localStorage.setItem('accessToken', token);
  }

  getToken(): string {
    return this.localStorage.getItem('accessToken') ?? '';
  }

  setRole(role: string) {
    if (role) {
      this.localStorage.setItem('userRole', role);
    } else {
      this.localStorage.removeItem('userRole');
    }
  }

  getRole() {
    const role = this.localStorage.getItem('userRole');
    if (role) {
      return role
    } else {
      return null;
    }
  }

  studentRegister(data: any): Observable<any> {
    return this._httpClient.post('auth/student-register', data);
  }

  tutorRegister(data: any): Observable<any> {
    return this._httpClient.post('auth/tutor-register', data);
  }

  verifyOtp(data: any): Observable<any> {
    return this._httpClient.post('auth/verify-otp', data).pipe(
      switchMap((response: any) => {
        if (response && response.success && response.data && response.data.access_token) {
          this._commonService.clearCache(); // Ensure fresh session
          this.setToken(response.data.access_token);
          this.setRole(response.data.role);
          this._authenticated = true;
        }
        return of(response);
      })
    );
  }

  resendOtp(data: any): Observable<any> {
    return this._httpClient.post('auth/resend-otp', data);
  }

  forgetPassword(email: string): Observable<any> {
    return this._httpClient.post('auth/send-otp', {
      channel: 'email',
      identifier: email,
      purpose: 'forgot_password'
    });
  }

  resetPassword(data: { reset_token: string; new_password: string }): Observable<any> {
    return this._httpClient.post('auth/reset-password', data);
  }

  login(credentials: { email: string; password: string }): Observable<any> {
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this._httpClient.post('auth/login', credentials).pipe(
      switchMap((response: any) => {
        if (response && response.success && response.data && response.data.access_token) {
          this._commonService.clearCache(); // Ensure fresh session
          this.setToken(response.data.access_token);
          this.setRole(response.data.role);
          this._authenticated = true;
        }
        return of(response);
      })
    );
  }

  signOut(): Observable<any> {
    this.localStorage.clear();
    this._authenticated = false;
    // Note: CommonService.clearCache() is called by the components during logout 
    // to ensure the singleton state is reset.
    return of(true);
  }

  check(): Observable<boolean> {
    if (!isPlatformBrowser(this._platformId)) {
      return of(true);
    }

    if (this._authenticated) {
      return of(true);
    }

    if (!this.getToken()) {
      return of(false);
    }

    // You might want to add a call to your backend to verify the token and get user info
    // For now, we'll just trust the token and get the role from local storage
    if (this.getRole()) {
        this._authenticated = true;
        return of(true);
    }

    return of(false);
  }
}